<template>
  <div class="field-palette">
    <!-- Tabs -->
    <div class="palette-tabs">
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'library' }"
        @click="activeTab = 'library'"
      >
        <i class="bx bx-library"></i>
        Biblioteca
      </button>
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'custom' }"
        @click="activeTab = 'custom'"
      >
        <i class="bx bx-plus-circle"></i>
        Customizado
      </button>
    </div>

    <!-- Search -->
    <div class="palette-search">
      <i class="bx bx-search"></i>
      <input
        v-model="search"
        type="text"
        :placeholder="
          activeTab === 'library' ? 'Buscar campos...' : 'Buscar tipos...'
        "
      />
      <button v-if="search" class="clear-search" @click="search = ''">
        <i class="bx bx-x"></i>
      </button>
    </div>

    <!-- Library Tab Content -->
    <div v-if="activeTab === 'library'" class="palette-content">
      <div
        v-for="category in filteredLibraryCategories"
        :key="category.key"
        class="field-category"
      >
        <div
          class="category-header"
          :class="{ expanded: expandedCategories[category.key] }"
          @click="toggleCategory(category.key)"
        >
          <div class="category-info">
            <i :class="category.icon || 'bx bx-folder'"></i>
            <span>{{ category.label }}</span>
          </div>
          <div class="category-meta">
            <span class="field-count">{{ category.fields.length }}</span>
            <i class="bx bx-chevron-down expand-icon"></i>
          </div>
        </div>

        <div v-show="expandedCategories[category.key]" class="category-fields">
          <div
            v-for="field in category.fields"
            :key="field.name"
            class="field-item"
            draggable="true"
            @dragstart="onDragStart($event, field, 'library')"
            @click="addLibraryField(field)"
          >
            <div class="field-icon">
              <i :class="getFieldIcon(field.fieldTypeName)"></i>
            </div>
            <div class="field-info">
              <span class="field-name">{{ field.label }}</span>
              <span v-if="field.placeholder" class="field-type">{{
                field.placeholder
              }}</span>
            </div>
            <button class="btn-add" title="Adicionar campo">
              <i class="bx bx-plus"></i>
            </button>
          </div>
        </div>
      </div>

      <div v-if="filteredLibraryCategories.length === 0" class="empty-state">
        <i class="bx bx-search-alt"></i>
        <p>Nenhum campo encontrado</p>
      </div>
    </div>

    <!-- Custom Tab Content -->
    <div v-else class="palette-content">
      <!-- Step 1: Select Field Format -->
      <div v-if="!customFieldForm.selectedType" class="custom-select-type">
        <div v-if="loading" class="loading-state">
          <b-spinner small></b-spinner>
          <span>Carregando tipos...</span>
        </div>

        <template v-else>
          <div class="custom-intro">
            <p>Selecione o formato do campo:</p>
          </div>

          <div class="field-types-list">
            <div
              v-for="fieldType in filteredFieldFormats"
              :key="fieldType.id"
              class="field-item field-type-item"
              @click="selectFieldFormat(fieldType)"
            >
              <div class="field-icon">
                <i :class="getFieldIcon(fieldType.name)"></i>
              </div>
              <div class="field-info">
                <span class="field-name">{{ fieldType.label }}</span>
                <span class="field-type-desc">{{
                  getCategoryLabel(fieldType.category)
                }}</span>
              </div>
              <i class="bx bx-chevron-right"></i>
            </div>
          </div>

          <div
            v-if="filteredFieldFormats.length === 0 && !loading"
            class="empty-state"
          >
            <i class="bx bx-search-alt"></i>
            <p>Nenhum formato encontrado</p>
          </div>
        </template>
      </div>

      <!-- Step 2: Configure Field -->
      <div v-else class="custom-configure-field">
        <div class="config-header">
          <button class="btn-back-config" @click="cancelCustomField">
            <i class="bx bx-arrow-back"></i>
          </button>
          <div class="config-title">
            <i :class="getFieldIcon(customFieldForm.selectedType.name)"></i>
            <span>{{ customFieldForm.selectedType.label }}</span>
          </div>
        </div>

        <div class="config-form">
          <div class="form-group">
            <label>Label do Campo *</label>
            <input
              v-model="customFieldForm.label"
              type="text"
              class="form-control form-control-sm"
              placeholder="Ex: Nome Completo"
            />
          </div>

          <div class="form-group">
            <label>Placeholder</label>
            <input
              v-model="customFieldForm.placeholder"
              type="text"
              class="form-control form-control-sm"
              placeholder="Texto de exemplo..."
            />
          </div>

          <div class="form-group">
            <label>Texto de Ajuda</label>
            <input
              v-model="customFieldForm.help_text"
              type="text"
              class="form-control form-control-sm"
              placeholder="Instruções para o usuário..."
            />
          </div>

          <!-- Options for select, radio, checkbox -->
          <div v-if="customFieldTypeHasOptions" class="form-group">
            <label>Opções</label>
            <div class="options-list">
              <div
                v-for="(opt, index) in customFieldForm.options"
                :key="index"
                class="option-item"
              >
                <input
                  v-model="opt.label"
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Label"
                  @input="updateOptionValue(index)"
                />
                <button
                  class="btn-remove-option"
                  @click="removeOption(index)"
                  v-if="customFieldForm.options.length > 1"
                >
                  <i class="bx bx-x"></i>
                </button>
              </div>
              <button class="btn-add-option" @click="addOption">
                <i class="bx bx-plus"></i> Adicionar opção
              </button>
            </div>
          </div>

          <div class="form-group">
            <label class="checkbox-label">
              <input type="checkbox" v-model="customFieldForm.required" />
              <span>Campo obrigatório</span>
            </label>
          </div>

          <div class="config-actions">
            <button class="btn-cancel" @click="cancelCustomField">
              Cancelar
            </button>
            <button
              class="btn-create"
              @click="createCustomField"
              :disabled="!customFieldForm.label"
            >
              <i class="bx bx-plus"></i>
              Adicionar Campo
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { fieldLibrary } from '../fieldLibrary'

export default {
  name: 'FieldPalette',
  props: {
    fieldTypesGrouped: {
      type: [Object, Array],
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      activeTab: 'library',
      search: '',
      expandedCategories: {},
      customFieldForm: {
        selectedType: null,
        label: '',
        placeholder: '',
        help_text: '',
        required: false,
        options: [{ label: '', value: '' }]
      },
      categoryLabels: {
        basic: 'Campos Básicos',
        selection: 'Campos de Escolha',
        brazilian: 'Campos Brasileiros',
        datetime: 'Data e Hora',
        file: 'Arquivos',
        advanced: 'Campos Avançados',
        layout: 'Elementos de Layout'
      },
      fieldIcons: {
        text: 'bx bx-text',
        textarea: 'bx bx-align-left',
        email: 'bx bx-envelope',
        phone: 'bx bx-phone',
        number: 'bx bx-hash',
        password: 'bx bx-lock',
        hidden: 'bx bx-hide',
        url: 'bx bx-link',
        select: 'bx bx-chevron-down',
        multiselect: 'bx bx-list-check',
        checkbox: 'bx bx-checkbox-checked',
        radio: 'bx bx-radio-circle-marked',
        toggle: 'bx bx-toggle-right',
        cpf: 'bx bx-id-card',
        cnpj: 'bx bx-building',
        cpf_cnpj: 'bx bx-id-card',
        cep: 'bx bx-map-pin',
        state: 'bx bx-map',
        city: 'bx bx-buildings',
        rg: 'bx bx-id-card',
        date: 'bx bx-calendar',
        time: 'bx bx-time',
        datetime: 'bx bx-calendar-event',
        birthdate: 'bx bx-cake',
        file: 'bx bx-upload',
        image: 'bx bx-image',
        rating: 'bx bx-star',
        slider: 'bx bx-slider',
        color: 'bx bx-palette',
        terms: 'bx bx-check-shield',
        heading: 'bx bx-heading',
        paragraph: 'bx bx-paragraph',
        divider: 'bx bx-minus',
        spacer: 'bx bx-move-vertical'
      }
    }
  },
  computed: {
    libraryCategories () {
      return Object.keys(fieldLibrary).map(key => ({
        key,
        label: fieldLibrary[key].label,
        icon: fieldLibrary[key].icon,
        fields: fieldLibrary[key].fields
      }))
    },

    filteredLibraryCategories () {
      if (!this.search) {
        return this.libraryCategories
      }
      const searchLower = this.search.toLowerCase()
      return this.libraryCategories
        .map(cat => ({
          ...cat,
          fields: cat.fields.filter(
            f =>
              f.label.toLowerCase().includes(searchLower) ||
              (f.name && f.name.toLowerCase().includes(searchLower))
          )
        }))
        .filter(cat => cat.fields.length > 0)
    },

    allFieldTypes () {
      const types = []
      if (this.fieldTypesGrouped && typeof this.fieldTypesGrouped === 'object') {
        if (Array.isArray(this.fieldTypesGrouped)) {
          // API returns array of { category, label, types: [...] }
          this.fieldTypesGrouped.forEach(category => {
            if (category.types && Array.isArray(category.types)) {
              types.push(...category.types)
            }
          })
        } else {
          // Object format: { category: [...types] }
          Object.values(this.fieldTypesGrouped).forEach(categoryTypes => {
            if (Array.isArray(categoryTypes)) {
              types.push(...categoryTypes)
            }
          })
        }
      }
      return types
    },

    filteredFieldFormats () {
      if (!this.search) {
        return this.allFieldTypes
      }
      const searchLower = this.search.toLowerCase()
      return this.allFieldTypes.filter(
        f =>
          f.label.toLowerCase().includes(searchLower) ||
          f.name.toLowerCase().includes(searchLower)
      )
    },

    customFieldTypeHasOptions () {
      if (!this.customFieldForm.selectedType) return false
      if (typeof this.customFieldForm.selectedType.has_options === 'boolean') {
        return this.customFieldForm.selectedType.has_options
      }
      const optionTypes = ['select', 'multiselect', 'radio', 'checkbox']
      return optionTypes.includes(this.customFieldForm.selectedType.name)
    }
  },
  mounted () {
    this.libraryCategories.forEach((cat, index) => {
      this.$set(this.expandedCategories, cat.key, index === 0)
    })
  },
  methods: {
    toggleCategory (key) {
      this.$set(this.expandedCategories, key, !this.expandedCategories[key])
    },

    getCategoryLabel (category) {
      return this.categoryLabels[category] || category
    },

    getFieldIcon (fieldTypeName) {
      return this.fieldIcons[fieldTypeName] || 'bx bx-text'
    },

    addLibraryField (field) {
      // Find the field type from the grouped types
      const fieldType = this.allFieldTypes.find(
        t => t.name === field.fieldTypeName
      )
      this.$emit('add-library-field', {
        ...field,
        fieldType,
        field_type_id: fieldType?.id
      })
    },

    onDragStart (event, item, source) {
      const fieldType = this.allFieldTypes.find(
        t => t.name === item.fieldTypeName
      )
      const data = {
        source,
        item: {
          ...item,
          fieldType,
          field_type_id: fieldType?.id
        }
      }
      event.dataTransfer.setData('application/json', JSON.stringify(data))
      event.dataTransfer.effectAllowed = 'copy'
    },

    selectFieldFormat (format) {
      this.customFieldForm.selectedType = format
      this.customFieldForm.label = ''
      this.customFieldForm.placeholder = ''
      this.customFieldForm.help_text = ''
      this.customFieldForm.required = false
      this.customFieldForm.options = [{ label: '', value: '' }]
    },

    cancelCustomField () {
      this.customFieldForm.selectedType = null
      this.customFieldForm.label = ''
      this.customFieldForm.placeholder = ''
      this.customFieldForm.help_text = ''
      this.customFieldForm.required = false
      this.customFieldForm.options = [{ label: '', value: '' }]
    },

    createCustomField () {
      if (!this.customFieldForm.label) return

      const fieldType = this.customFieldForm.selectedType

      let options = null
      if (this.customFieldTypeHasOptions) {
        options = this.customFieldForm.options
          .filter(opt => opt.label.trim())
          .map(opt => ({
            label: opt.label,
            value: opt.value || this.slugify(opt.label)
          }))
        if (options.length === 0) {
          options = [{ label: 'Opção 1', value: 'opcao_1' }]
        }
      }

      const customField = {
        field_type_id: fieldType.id,
        fieldType: fieldType,
        label: this.customFieldForm.label,
        placeholder: this.customFieldForm.placeholder,
        help_text: this.customFieldForm.help_text,
        required: this.customFieldForm.required,
        options: options,
        config: fieldType.default_config,
        validations: fieldType.default_validations
      }

      this.$emit('add-field', customField)
      this.cancelCustomField()
    },

    addOption () {
      this.customFieldForm.options.push({ label: '', value: '' })
    },

    removeOption (index) {
      this.customFieldForm.options.splice(index, 1)
    },

    updateOptionValue (index) {
      const opt = this.customFieldForm.options[index]
      if (opt.label && !opt.value) {
        opt.value = this.slugify(opt.label)
      }
    },

    slugify (text) {
      return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '')
    }
  }
}
</script>

<style scoped>
.field-palette {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
}

/* Tabs */
.palette-tabs {
  display: flex;
  border-bottom: 1px solid #e9ecef;
}

.tab-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px 8px;
  border: none;
  background: transparent;
  font-size: 13px;
  font-weight: 500;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.15s;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
}

.tab-btn:hover {
  color: #495057;
  background: #f8f9fa;
}

.tab-btn.active {
  color: #556ee6;
  border-bottom-color: #556ee6;
  background: transparent;
}

.tab-btn i {
  font-size: 16px;
}

/* Search */
.palette-search {
  padding: 12px 16px;
  position: relative;
  border-bottom: 1px solid #e9ecef;
}

.palette-search > i {
  position: absolute;
  left: 28px;
  top: 50%;
  transform: translateY(-50%);
  color: #adb5bd;
  font-size: 16px;
}

.palette-search input {
  width: 100%;
  padding: 10px 36px 10px 40px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 13px;
  background: #f8f9fa;
  transition: all 0.15s;
}

.palette-search input:focus {
  outline: none;
  border-color: #556ee6;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(85, 110, 230, 0.1);
}

.clear-search {
  position: absolute;
  right: 28px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: #adb5bd;
  cursor: pointer;
  padding: 4px;
}

.clear-search:hover {
  color: #495057;
}

/* Content */
.palette-content {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  scrollbar-width: thin;
  scrollbar-color: #d0d5dd transparent;
}

.palette-content::-webkit-scrollbar {
  width: 6px;
}

.palette-content::-webkit-scrollbar-track {
  background: transparent;
}

.palette-content::-webkit-scrollbar-thumb {
  background-color: #d0d5dd;
  border-radius: 3px;
}

/* Category */
.field-category {
  margin-bottom: 4px;
}

.category-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #f8f9fa;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
}

.category-header:hover {
  background: #f0f2f5;
}

.category-info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #495057;
}

.category-info i {
  font-size: 16px;
  color: #556ee6;
}

.category-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}

.field-count {
  font-size: 11px;
  font-weight: 500;
  color: #6c757d;
  background: #e9ecef;
  padding: 2px 8px;
  border-radius: 10px;
}

.expand-icon {
  font-size: 18px;
  color: #adb5bd;
  transition: transform 0.2s;
}

.category-header.expanded .expand-icon {
  transform: rotate(180deg);
}

/* Fields */
.category-fields {
  padding: 8px 0 8px 8px;
}

.field-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  margin-bottom: 6px;
  cursor: pointer;
  transition: all 0.15s;
}

.field-item:last-child {
  margin-bottom: 0;
}

.field-item:hover {
  border-color: #556ee6;
  background: #f8f9ff;
  box-shadow: 0 2px 4px rgba(85, 110, 230, 0.1);
}

.field-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f4ff;
  border-radius: 8px;
  flex-shrink: 0;
}

.field-icon i {
  font-size: 16px;
  color: #556ee6;
}

.field-info {
  flex: 1;
  min-width: 0;
}

.field-name {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #344054;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.field-type {
  display: block;
  font-size: 11px;
  color: #9ca3af;
  margin-top: 2px;
}

.field-type-desc {
  display: block;
  font-size: 10px;
  color: #9ca3af;
  margin-top: 2px;
}

.btn-add {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #556ee6;
  color: #fff;
  border-radius: 6px;
  cursor: pointer;
  opacity: 0;
  transition: all 0.15s;
  flex-shrink: 0;
}

.field-item:hover .btn-add {
  opacity: 1;
}

.btn-add:hover {
  background: #485ec4;
}

/* States */
.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px;
  color: #6c757d;
  font-size: 13px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 16px;
  text-align: center;
}

.empty-state i {
  font-size: 32px;
  color: #d0d5dd;
  margin-bottom: 8px;
}

.empty-state p {
  margin: 0;
  font-size: 13px;
  color: #9ca3af;
}

/* Custom Field Configuration */
.custom-intro {
  padding: 8px 12px;
  margin-bottom: 8px;
}

.custom-intro p {
  margin: 0;
  font-size: 12px;
  color: #6c757d;
}

.field-types-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-types-list .field-item {
  margin-bottom: 0;
}

.field-type-item > i.bx-chevron-right {
  color: #adb5bd;
  font-size: 18px;
  flex-shrink: 0;
}

.field-type-item:hover > i.bx-chevron-right {
  color: #556ee6;
}

.custom-configure-field {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.config-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.btn-back-config {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e9ecef;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  color: #495057;
  transition: all 0.15s;
}

.btn-back-config:hover {
  border-color: #556ee6;
  color: #556ee6;
}

.config-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #344054;
}

.config-title i {
  font-size: 18px;
  color: #556ee6;
}

.config-form {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
}

.config-form .form-group {
  margin-bottom: 16px;
}

.config-form label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #495057;
  margin-bottom: 6px;
}

.checkbox-label {
  display: flex !important;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.checkbox-label input[type='checkbox'] {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.checkbox-label span {
  font-size: 13px;
  color: #495057;
}

/* Options List */
.options-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.option-item {
  display: flex;
  gap: 8px;
  align-items: center;
}

.option-item .form-control-sm {
  flex: 1;
}

.btn-remove-option {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #fee2e2;
  color: #dc2626;
  border-radius: 4px;
  cursor: pointer;
  flex-shrink: 0;
}

.btn-remove-option:hover {
  background: #fecaca;
}

.btn-add-option {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px;
  border: 1px dashed #d0d5dd;
  background: transparent;
  border-radius: 6px;
  font-size: 12px;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-add-option:hover {
  border-color: #556ee6;
  color: #556ee6;
}

/* Config Actions */
.config-actions {
  display: flex;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
  margin-top: 16px;
}

.btn-cancel {
  flex: 1;
  padding: 10px 16px;
  border: 1px solid #e9ecef;
  background: #fff;
  border-radius: 6px;
  font-size: 13px;
  color: #495057;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-cancel:hover {
  border-color: #d0d5dd;
  background: #f8f9fa;
}

.btn-create {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 16px;
  border: none;
  background: #556ee6;
  color: #fff;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-create:hover:not(:disabled) {
  background: #485ec4;
}

.btn-create:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
